// components/FAQ.tsx
"use client";

import { useState } from 'react';
import { FaPlus, FaMinus } from 'react-icons/fa';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

interface FAQProps {
  title?: string;
  subtitle?: string;
  items?: FAQItem[];
}

const defaultFAQs: FAQItem[] = [
  {
    id: '1',
    question: 'What services does Movezze offer?',
    answer: 'Movezze offers a wide range of relocation services including household shifting, bike transport, car carrier, office relocation, pet relocation, and international relocation services across India. We also provide packing, unpacking, and warehousing services.',
  },
  {
    id: '2',
    question: 'How much does household shifting cost?',
    answer: 'The cost depends on the distance, volume of goods, and the service type (e.g., full packing, partial packing). On average, household shifting starts from ₹5,000 for local moves and goes up to ₹25,000+ for intercity moves. We provide free estimates through our enquiry form.',
  },
  {
    id: '3',
    question: 'Are my belongings insured during transit?',
    answer: 'Yes, all shipments with Movezze are fully insured. We provide comprehensive insurance coverage for your belongings during transit, giving you peace of mind. The insurance value is based on the estimated value of your goods.',
  },
  {
    id: '4',
    question: 'Can I track my shipment in real-time?',
    answer: 'Yes, we provide real-time GPS tracking for all shipments. You will receive a tracking ID after booking, which you can use to monitor your shipment\'s location and estimated delivery time.',
  },
  {
    id: '5',
    question: 'How do I book a service?',
    answer: 'You can book a service through our website by filling out the enquiry form, or by calling us directly at +91 8130 880 900. Our team will assist you with the booking process and provide a free estimate.',
  },
  {
    id: '6',
    question: 'What types of vehicles do you transport?',
    answer: 'We transport all types of vehicles including cars, SUVs, bikes, scooters, and even luxury vehicles. We use specialized carriers with protective packaging to ensure safe delivery.',
  },
  {
    id: '7',
    question: 'Do you provide packing materials and assistance?',
    answer: 'Yes, we provide professional packing materials such as boxes, bubble wrap, and tape. Our trained team can also assist with packing and unpacking your belongings to ensure everything is secure for transit.',
  },
  {
    id: '8',
    question: 'How far in advance should I book?',
    answer: 'We recommend booking at least 3-5 days in advance for local moves and 7-10 days for intercity or international moves. This allows us to schedule the best resources for your move.',
  },
];

export default function FAQ({
  title = "Have Questions? We Have Answers",
  subtitle = "Read our most frequently asked questions about moving and relocation",
  items = defaultFAQs,
}: FAQProps) {
  const [openId, setOpenId] = useState<string | null>(null);

  const toggle = (id: string) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section className="py-12 md:py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-[#292929]">{title}</h2>
          <p className="text-[#343434] mt-2 text-base md:text-lg">{subtitle}</p>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-3">
          {items.map((item) => {
            const isOpen = openId === item.id;
            return (
              <div
                key={item.id}
                className="border border-gray-200 rounded-md overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              >
                <button
                  onClick={() => toggle(item.id)}
                  className="w-full flex items-center justify-between px-5 md:px-6 py-4 md:py-5 text-left bg-white hover:bg-[#f62b1e]/10 transition-colors"
                  aria-expanded={isOpen}
                >
                  <span className="text-sm md:text-base font-semibold text-[#343434] pr-4">
                    {item.question}
                  </span>
                  <span className="flex-shrink-0 ml-4">
                    {isOpen ? (
                      <FaMinus className="text-[#f62b1e] text-sm" />
                    ) : (
                      <FaPlus className="text-[#f62b1e] text-sm" />
                    )}
                  </span>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-5 md:px-6 pb-4 md:pb-5 pt-0 text-sm md:text-base text-[#343434] leading-relaxed border-t border-gray-100">
                    {item.answer}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer CTA */}
        <div className="text-center mt-10">
          <p className="text-[#343434] text-sm">
            Still have questions?{' '}
            <a
              href="/contact"
              className="text-[#343434] font-medium hover:text-[#f62b1e] transition-colors"
            >
              Contact us
            </a>
            {' '}or call{' '}
            <a
              href="tel:+918130880900"
              className="text-[#343434] font-medium hover:text-[#f62b1e] transition-colors"
            >
              +91 8130 880 900
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}